/**
 * TET À TET — MENU FLIPBOOK
 * Bladerbare menukaart. Swipe op mobiel, klikken op desktop.
 * Responsive: 1 page view mobile, 2-page spread desktop.
 */
(function () {
  'use strict';

  const mql = window.matchMedia('(min-width: 768px)');

  class Flipbook {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-flipbook-track]');
      this.stage = root.querySelector('[data-flipbook-stage]');
      this.pages = [...root.querySelectorAll('.flipbook__page')];
      this.prevBtn = root.querySelector('[data-flipbook-prev]');
      this.nextBtn = root.querySelector('[data-flipbook-next]');
      this.progressItems = [...root.querySelectorAll('[data-flipbook-progress] span')];
      this.tabs = [...root.querySelectorAll('[data-goto-page]')];
      this.totalPages = this.pages.length;

      this.currentPage = 0;
      this.isDesktop = mql.matches;
      this.isAnimating = false;

      this.hydrateItems();
      this.bindEvents();
      this.updateState();
    }

    hydrateItems() {
      this.pages.forEach(page => {
        const dataNode = page.querySelector('[data-items-json]');
        const ssrNode = page.querySelector('[data-items-ssr]');
        if (!dataNode) return;
        try {
          const items = JSON.parse(dataNode.textContent.trim());
          if (!Array.isArray(items) || !items.length) return;
          const html = items.map(renderItem).join('');
          if (ssrNode) ssrNode.innerHTML = html;
          else page.querySelector('.flipbook__page-items')?.insertAdjacentHTML('beforeend', html);
        } catch (e) {
          console.warn('Flipbook: kon items JSON niet parsen voor', page.dataset.page, e);
        }
      });

      function renderItem(it) {
        const name = escapeHtml(it.name || '');
        const desc = it.desc ? `<div class="menu-item__desc">${escapeHtml(it.desc)}</div>` : '';
        const price = escapeHtml(it.price || '');
        let badges = '';
        if (Array.isArray(it.tags)) {
          badges = it.tags.map(t => {
            if (t === 'veg')   return '<span class="badge badge--veg" title="Vegetarisch">🌿</span>';
            if (t === 'vegan') return '<span class="badge badge--vegan" title="Vegan">🌱 V</span>';
            return '';
          }).join('');
        }
        return `
          <div class="menu-item">
            <div class="menu-item__top">
              <span class="menu-item__name">${name}${badges ? ' <span class="badges">' + badges + '</span>' : ''}</span>
              <span class="menu-item__dots" aria-hidden="true"></span>
              <span class="menu-item__price">€ ${price}</span>
            </div>
            ${desc}
          </div>`;
      }
      function escapeHtml(s) {
        return String(s)
          .replace(/&/g, '&amp;').replace(/</g, '&lt;')
          .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      }
    }

    bindEvents() {
      this.prevBtn?.addEventListener('click', () => this.prev());
      this.nextBtn?.addEventListener('click', () => this.next());

      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const p = parseInt(tab.dataset.gotoPage, 10);
          if (!isNaN(p)) this.goTo(p);
        });
      });

      this.root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this.prev(); }
      });
      this.root.setAttribute('tabindex', '0');

      this.bindSwipe();

      this.stage.addEventListener('click', (e) => {
        if (!this.isDesktop) return;
        if (e.target.closest('.menu-item, .flipbook__tab, button, a')) return;
        const rect = this.stage.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const edge = rect.width * 0.18;
        if (x < edge) this.prev();
        else if (x > rect.width - edge) this.next();
      });

      mql.addEventListener('change', () => {
        this.isDesktop = mql.matches;
        this.updateState();
      });
    }

    bindSwipe() {
      let startX = 0, startY = 0, dx = 0, dy = 0, pointerDown = false;
      const threshold = 40;

      this.stage.addEventListener('pointerdown', (e) => {
        if (this.isAnimating) return;
        pointerDown = true;
        startX = e.clientX; startY = e.clientY; dx = 0; dy = 0;
        this.stage.setPointerCapture?.(e.pointerId);
      });
      this.stage.addEventListener('pointermove', (e) => {
        if (!pointerDown) return;
        dx = e.clientX - startX;
        dy = e.clientY - startY;
      });
      const release = () => {
        if (!pointerDown) return;
        pointerDown = false;
        if (Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) this.next();
          else this.prev();
        }
      };
      this.stage.addEventListener('pointerup', release);
      this.stage.addEventListener('pointercancel', release);
      this.stage.addEventListener('pointerleave', release);
    }

    getMaxIndex() {
      return this.isDesktop ? Math.max(0, this.totalPages - 2) : this.totalPages - 1;
    }
    getStep() { return this.isDesktop ? 2 : 1; }

    next() {
      if (this.isAnimating) return;
      const next = Math.min(this.currentPage + this.getStep(), this.getMaxIndex());
      if (next !== this.currentPage) this.goTo(next);
    }
    prev() {
      if (this.isAnimating) return;
      const next = Math.max(this.currentPage - this.getStep(), 0);
      if (next !== this.currentPage) this.goTo(next);
    }
    goTo(pageIndex) {
      const max = this.totalPages - 1;
      this.currentPage = Math.max(0, Math.min(pageIndex, max));
      if (this.isDesktop && this.currentPage % 2 === 1) this.currentPage -= 1;
      this.updateState();
    }

    updateState() {
      this.isAnimating = true;

      if (this.isDesktop) {
        this.track.style.transform = `translate3d(${-(this.currentPage / 2) * 100}%, 0, 0)`;
      } else {
        this.track.style.transform = `translate3d(${-this.currentPage * 100}%, 0, 0)`;
      }

      this.progressItems.forEach((el, i) => {
        if (this.isDesktop) {
          el.classList.toggle('is-current', i === this.currentPage || i === this.currentPage + 1);
        } else {
          el.classList.toggle('is-current', i === this.currentPage);
        }
      });

      this.tabs.forEach(tab => {
        const tabPage = parseInt(tab.dataset.gotoPage, 10);
        const active = this.isDesktop
          ? (tabPage === this.currentPage || tabPage === this.currentPage + 1)
          : (tabPage === this.currentPage);
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-current', active ? 'true' : 'false');
      });

      this.pages.forEach((p, i) => {
        const visible = this.isDesktop
          ? (i === this.currentPage || i === this.currentPage + 1)
          : (i === this.currentPage);
        p.setAttribute('aria-hidden', visible ? 'false' : 'true');
      });

      if (this.prevBtn) this.prevBtn.disabled = this.currentPage === 0;
      if (this.nextBtn) this.nextBtn.disabled = this.currentPage >= this.getMaxIndex();

      setTimeout(() => { this.isAnimating = false; }, 650);
    }
  }

  const init = () => {
    document.querySelectorAll('[data-flipbook]').forEach(el => new Flipbook(el));
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
